/* ============================================================================
   ROVE NEXT product card behaviour.

   WHAT CHANGED, 24 Aug 2026 — ALL COPY REWRITING REMOVED.

   This file used to overwrite, on every page load:
     · the Find Your NEXT Form heading and its intro paragraph
     · the tagline and feature line on all three product cards
     · the product title on the Reya card
     · the price, by stripping a leading "From "

   All of it was hardcoded here, which meant a merchant editing any of those
   fields in the Theme Editor watched the change revert a fraction of a second
   after the page painted. Those strings now live in templates/index.json and
   are editable where they should be.

   THE "From " STRIP IS GONE AND IS NOT COMING BACK. Ophelia starts at $1,695
   and Reya at $2,150; both have higher configurations. Removing "From" printed
   an entry price as if it were the only price. Keeping it is both more honest
   and the standard ecommerce convention.

   What remains is the one thing here that was never copy: making the colourway
   dots on the product cards actually swap the photograph.

   ============================================================================
   THE PHONE GUARD CAME OFF ON 9 SEPTEMBER 2026, AND THAT IS THE BUG FIX.

   Sharon: "clicking on the different colourways should change it to the
   different images and it's not changing" — on desktop. She also confirmed the
   same dots work in Ready Sooner Than You Think, which is what made this file
   the answer rather than the section.

   sections/rn-hp-cards.liquid carries its own delegated handler that does this
   job, and on desktop it is the ONLY thing bound. It does not work, and the
   proof that it has not worked for some time is this very file: it exists
   because the dots also failed on a phone, and whoever hit that in August
   patched the phone rather than finding out why the section's own handler was
   dead. The guard below was the whole reason desktop stayed broken — one
   matchMedia call quietly limiting the fix to 600px and under.

   The cause inside the section is still unknown. Everything checkable there is
   correct: the dots render as real <button> elements (both conditions in the
   Liquid are met), every swatch carries a distinct photo, data-src is
   populated, .hp-dots sits at z-index 4 above the CTA's click overlay, and
   .hp-card exists so dot.closest('.hp-card') resolves. Finding out why it never
   fires needs a browser console on the rendered page, which is not available
   while the storefront is password-gated.

   So this handler is now the one that runs, at every width. It keeps
   stopImmediatePropagation, so if the section's handler ever starts working
   the two cannot both swap the same image and fight each other. If someone
   later finds the root cause in the section, delete this listener rather than
   leaving two.
   ============================================================================ */
(function(){
  /* ==========================================================================
     A CHOSEN COLOURWAY OUTRANKS THE ROOM SHOT.

     Sharon, 9 September, after the swap was working: Ophelia changes colour
     fine, Diane and Reya "super delayed ... very glitchy ... not working".

     Nothing was delayed and nothing was glitchy. Ophelia has NO staged scene
     photograph; Diane and Reya both do. The swap fired instantly on all three.
     But the scene is revealed whenever the cursor is on the photograph, so on
     Diane and Reya the sequence was: click a swatch, move onto the picture to
     look at the colour, and the room shot slides over the top of it. The new
     colourway was there the whole time, underneath. Counting to twenty while
     hovering never showed it, because leaving the photograph is the only thing
     that reveals it. The identical split exists in Ready Sooner Than You
     Think — its Diane and Reya cards carry scenes, its Ophelia card does not —
     which is why that section looked fine when Ophelia was the one tested.

     THE RULE THIS SETTLES. The scene is one room photograph per product; it
     does not change with the fabric. So the moment someone picks a colourway
     they have asked a question the scene cannot answer, and the product shot
     must win. `is-picking` holds it visible for as long as the pointer stays
     on that card, and clears when they leave — so the hover reveal is not lost,
     it simply stops covering an answer the customer just asked for.
     ========================================================================== */
  function holdShot(container){
    if(!container)return;
    container.classList.add('is-picking');
    if(container.__rnHoldBound)return;
    container.__rnHoldBound=true;
    container.addEventListener('mouseleave',function(){
      container.classList.remove('is-picking');
    });
  }

  /* Ready Sooner does its own swapping and that half works, so this only adds
     the hold. Bubble phase and no preventDefault, so it cannot interfere. */
  function bindReadySoonerHold(){
    if(window.__rnRtsHoldBound)return;
    window.__rnRtsHoldBound=true;
    document.addEventListener('click',function(ev){
      var d=ev.target&&ev.target.closest?ev.target.closest('[id^="rn-hp-rts-"] button.rs-dot'):null;
      if(d)holdShot(d.closest('.rs-item'));
    });
  }

  function bindCardSwatches(){
    if(window.__rnMobileCardSwatchesBound)return;
    window.__rnMobileCardSwatchesBound=true;
    document.addEventListener('click',function(ev){
      var target=ev.target;
      var dot=target&&target.closest?target.closest('[id^="rn-hp-cards-"] button.hp-dot'):null;
      if(!dot)return;
      var card=dot.closest('.hp-card');
      var media=card&&card.querySelector('.hp-media');
      /* .hp-shot explicitly. A card with a staged scene holds TWO images in
         this box and a bare querySelector('img') takes whichever is first in
         the DOM — today that is the product shot, but the scene layer is only
         rendered conditionally, so relying on document order here would be a
         trap for whoever next edits the markup. Swapping the scene instead of
         the shot would replace the room photograph with a product cut-out on
         hover, which is a much more confusing failure than no swap at all. */
      var img=media&&(media.querySelector('img.hp-shot')||media.querySelector('img'));
      var src=dot.getAttribute('data-src')||'';
      if(!card||!img||!src)return;

      ev.preventDefault();
      ev.stopPropagation();
      if(ev.stopImmediatePropagation)ev.stopImmediatePropagation();

      card.querySelectorAll('button.hp-dot').forEach(function(d){
        d.setAttribute('aria-pressed',d===dot?'true':'false');
      });
      holdShot(card);

      var set=dot.getAttribute('data-srcset')||'';
      var alt=dot.getAttribute('data-alt')||img.alt||'';

      /* Decoded before it is shown, so the card dims for a beat rather than
         painting a half-loaded photograph — the same courtesy the section's
         handler intended. is-swapping is the section's own class and it is
         always removed again, including when the image fails to load, so a
         broken file can never leave a card stuck at 35% opacity. */
      media.classList.add('is-swapping');
      var pre=new Image();
      pre.onload=pre.onerror=function(){
        if(set)img.setAttribute('srcset',set);else img.removeAttribute('srcset');
        img.setAttribute('src',src);
        img.setAttribute('alt',alt);
        media.classList.remove('is-swapping');
      };
      pre.src=src;
    },true);
  }

  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',function(){
      bindCardSwatches();bindReadySoonerHold();
    },{once:true});
  }else{
    bindCardSwatches();bindReadySoonerHold();
  }
})();

/* ============================================================================
   CLICK THE PHOTOGRAPH, GO TO THE PRODUCT. 9 Oct 2026, Stevie: on the homepage
   "Next Generation" cards (rn-hp-cards) and "Ready Sooner Than You Think"
   cards (rn-hp-readytoship), "we have a hover logic - we WANT TO KEEP THAT THE
   EXACT SAME but if they CLICK on the images and dont hover, take them to PDP
   - right now it does nothing if they click".

   WHY A CLICK DID NOTHING. Both sections meant the whole card to be clickable
   through a stretched ::after overlay on the Shop button (.hp-explore /
   .rs-shop). But that button is itself position:relative, so the overlay's
   inset:0 resolves against the BUTTON, not the card: it only ever covered the
   button. That is also exactly why the photo hover still worked -- nothing was
   on top of the photograph.

   THE FIX, ADDITIVE ONLY. A click on the photo box follows the same link as
   that card's own Shop button. No CSS hover rule, no overlay and no markup
   changes, so every hover (lifestyle swap, staged scene, push-in) behaves
   exactly as before. Colourway dots sit OUTSIDE the photo box and are real
   buttons, so they are never caught here. Ctrl/Cmd/Shift-click and a
   middle-click open the product in a new tab, like any normal link.
   ============================================================================ */
(function(){
  if(window.__rnCardImageClickBound)return;
  window.__rnCardImageClickBound=true;

  var MEDIA='[id^="rn-hp-cards-"] .hp-media, [id^="rn-hp-rts-"] .rs-media';

  function linkFor(t){
    if(!t||!t.closest)return null;
    var media=t.closest(MEDIA);
    if(!media)return null;
    if(t.closest('a, button, input, select, textarea'))return null;
    var card=media.closest('.hp-card, .rs-item');
    var a=card&&card.querySelector('a.hp-explore[href], a.rs-shop[href]');
    if(!a)return null;
    var h=a.getAttribute('href');
    if(!h||h==='#')return null;
    return a;
  }

  document.addEventListener('click',function(ev){
    if(ev.defaultPrevented)return;
    var a=linkFor(ev.target);
    if(!a)return;
    if(ev.metaKey||ev.ctrlKey||ev.shiftKey){window.open(a.href,'_blank');return;}
    window.location.href=a.href;
  });

  document.addEventListener('auxclick',function(ev){
    if(ev.button!==1)return;
    var a=linkFor(ev.target);
    if(a)window.open(a.href,'_blank');
  });

  /* Pointer cursor on the photo, so it reads as clickable. Cursor only --
     nothing visual about the hover changes. */
  var st=document.createElement('style');
  st.textContent='[id^="rn-hp-cards-"] .hp-media,[id^="rn-hp-rts-"] .rs-media{cursor:pointer}';
  (document.head||document.documentElement).appendChild(st);
})();
